<template>
    <form>
        <div class="row q-col-gutter-sm">
            <div class="col col-xs-12 col-md-6">
                <q-select
                    v-model="TransactionObj.transfer_reference"
                    :options="filtered_references"
                    use-input
                    hide-selected
                    fill-input
                    clearable
                    input-debounce="250"
                    option-value="reference"
                    option-label="reference"
                    map-options
                    emit-value
                    :label="$t('transactions.table_titles.transfer_reference')"
                    :error="TransactionObj.errors.has('transfer_reference')"
                    color="input-color"
                    @filter="filterReferences"
                    @input="$emit('referenced',TransactionObj.transfer_reference)"
                    :loading="loading"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                            style="border-bottom:1px solid #999"
                        >
                            <q-item-section top>
                                <q-item-label lines="1">
                                    <span class="text-green text-weight-medium">{{ scope.opt.reference }} - </span>
                                    <span class="text-body2">{{ scope.opt.status }}</span>
                                </q-item-label>
                                <q-item-label caption lines="1" class="text-red">
                                    <currency-format
                                        :value="scope.opt.amount"
                                        :unit="scope.opt.currency"
                                        hide-white-spaces
                                        hide-zero-decimal
                                    />
                                </q-item-label>
                                <q-item-label lines="1" class="text-body1 text-input-color">
                                    <span>{{ scope.opt.user }}</span>
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                    <template v-slot:no-option>
                        <q-item>
                            <q-item-section class="text-grey">
                                No Reference Found!
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- TRANSFER REFERENCE -->
            <div class="col col-xs-12 col-md-6">
                <q-input
                    v-model="TransactionObj.bank_date"
                    :label="$t('transactions.table_titles.bank_date')"
                    :error="TransactionObj.errors.has('bank_date')"
                    :error-message="TransactionObj.errors.get('bank_date')"
                    :rules="[ val => !!val || $t('transactions.table_titles.bank_date') + ' field is required']"
                    color="input-color"
                >
                    <template v-slot:prepend>
                        <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                            <q-popup-proxy ref="qDateStart1" transition-show="scale" transition-hide="scale">
                                <q-date v-model="TransactionObj.bank_date"
                                        mask="YYYY-MM-DD HH:mm"
                                        today-btn
                                        @input="() => $refs.qDateStart1.hide()"
                                />
                            </q-popup-proxy>
                        </q-icon>
                    </template>

                    <template v-slot:append>
                        <q-icon name="fa fa-clock" class="cursor-pointer">
                            <q-popup-proxy transition-show="scale" transition-hide="scale">
                                <q-time v-model="TransactionObj.bank_date" mask="YYYY-MM-DD HH:mm" format24h>
                                    <div class="row items-center justify-end">
                                        <q-btn v-close-popup label="Set" color="primary" flat />
                                    </div>
                                </q-time>
                            </q-popup-proxy>
                        </q-icon>
                    </template>
                </q-input>
            </div> <!-- BANK_DATE -->
            <div class="col col-xs-12 col-md-12">
                <q-input
                    v-model="TransactionObj.amount"
                    type="number"
                    input-class="input-bold"
                    :label="$t('transactions.table_titles.amount')"
                    lazy-rules
                    :error="TransactionObj.errors.has('amount')"
                    :error-message="TransactionObj.errors.get('amount')"
                    :rules="[ val => val && val > 0 || 'The ' + $t('transactions.table_titles.amount') + ' field is required']"
                    color="input-color"
                >
                    <template v-slot:append>
                        <q-select
                            v-model="TransactionObj.currency_acronym"
                            flat
                            :options="currencies"
                            emit-value
                            map-options
                            option-label="symbol"
                            option-value="acronym"
                            color="primary"
                            :loading="loading"
                        >
                            <template v-slot:option="scope">
                                <q-item
                                    v-bind="scope.itemProps"
                                    v-on="scope.itemEvents"
                                >
                                    <q-item-section>
                                        <q-item-label>{{scope.opt.symbol}}</q-item-label>
                                        <q-item-label caption>{{ scope.opt.name }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-select>
                    </template>
                </q-input>
            </div> <!-- AMOUNT -->
            <div class="col col-xs-12 col-md-6">
                <div v-if="TransactionObj.amount > 0">
                    <currency-format
                        :value="TransactionObj.amount"
                        class="text-h6 text-orange-12 inline-block"
                        hide-zero-decimal
                    />
                    <q-btn
                        round
                        flat
                        icon="far fa-copy"
                        @success="copied('The amount has copied!')"
                        v-clipboard="TransactionObj.amount"
                        dense
                        color="primary"
                        v-if="TransactionObj.currency_acronym !== 'RLS'"
                    />
                </div>
            </div> <!-- AMOUNT LABEL -->
            <div class="col col-xs-12 col-md-6">
                <div v-if="TransactionObj.amount > 0 && TransactionObj.currency_acronym === 'RLS'">
                    <currency-format
                        :value="TransactionObj.amount * 10"
                        class="text-h6 text-orange-12 inline-block"
                        hide-zero-decimal
                        unit="ریال"
                    />
                    <q-btn
                        round
                        flat
                        icon="far fa-copy"
                        @success="copied('The amount in rial has copied!')"
                        v-clipboard="TransactionObj.amount * 10"
                        color="primary"
                        dense
                    />
                </div>
            </div> <!-- AMOUNT LABEL RIAL -->
            <div class="col col-xs-12 col-md-6">
                <q-select
                    v-model="TransactionObj.outgoing_payment_method_id"
                    :options="payment_methods"
                    option-value="id"
                    option-label="type"
                    emit-value
                    map-options
                    :label="$t('transactions.table_titles.payment_method')"
                    :error="TransactionObj.errors.has('outgoing_payment_method_id')"
                    :error-message="TransactionObj.errors.get('outgoing_payment_method_id')"
                    :rules="[ val => !!val || $t('transactions.table_titles.payment_method') + ' is required']"
                    color="input-color"
                />
            </div> <!-- PAYMENT METHOD -->
            <div class="col col-xs-12 col-md-6">
                <q-select
                    v-model="TransactionObj.rebit_account_id"
                    :options="activeRebitAccountsByCurrency(TransactionObj.currency_acronym)"
                    fill-input
                    option-value="id"
                    option-label="label"
                    emit-value
                    map-options
                    :label="$t('transactions.table_titles.rebit_account')"
                    :error="TransactionObj.errors.has('rebit_account_id')"
                    :error-message="TransactionObj.errors.get('rebit_account_id')"
                    color="input-color"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                        >
                            <q-item-section>
                                <q-item-label>{{scope.opt.label}}</q-item-label>
                                <q-item-label caption>{{ scope.opt.holder }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
            </div> <!-- REBIT ACCOUNT ID -->
            <div class="col col-xs-12 col-md-12">
                <q-input
                    v-model="TransactionObj.payment_details"
                    :label="$t('transactions.table_titles.payee')"
                    :error="TransactionObj.errors.has('payment_details')"
                    :error-message="TransactionObj.errors.get('payment_details')"
                    color="input-color"
                />
            </div> <!-- PAYMENT DETAILS -->
            <div class="col col-xs-12">
                <q-input
                    v-model="TransactionObj.references"
                    :label="$t('transactions.table_titles.references')"
                    :error="TransactionObj.errors.has('references')"
                    :error-message="TransactionObj.errors.get('references')"
                    color="input-color"
                    autogrow
                />
            </div> <!-- REFERENCES -->
            <div class="col col-12">
                <q-input
                    v-model="TransactionObj.description"
                    :label="$t('transactions.table_titles.description')"
                    :error="TransactionObj.errors.has('description')"
                    :error-message="TransactionObj.errors.get('description')"
                    color="input-color"
                    autogrow
                />
            </div> <!-- DESCRIPTION -->
            <div class="col col-xs-12 col-md-6">
                <q-select
                    v-model="TransactionObj.status"
                    :options="statuses"
                    label="Status"
                    emit-value
                    map-options
                    option-label="label"
                    option-value="value"
                    color="input-color"
                />
            </div> <!-- STATUS -->
        </div>
    </form>
</template>

<script>
import User from "Modules/User/resources/models/User"
import {mapGetters, mapActions} from 'vuex'
export default {
    name: "OutgoingTransactionForm",
    model: {
        prop: 'TransactionObj'
    },
    props: {
        TransactionObj: {
            type: Object,
            required: true
        },
        references: {
            type: Array,
            default: () => ([])
        }
    },
    computed: {
        ...mapGetters(['activeRebitAccountsByCurrency', 'currencies', 'outgoing_payment_methods']),
        currency() {
            return this.TransactionObj.currency_acronym
        }
    },
    data() {
        return {
            loading: false,
            users: [],
            filtered_references: [],
            payment_methods: [],
            statuses: [
                {label: 'Pending', value: 'pending'},
                {label: 'Accepted', value: 'accepted'},
                {label: 'Declined', value: 'declined'},
                {label: 'Refunded', value: 'refunded'},
                {label: 'Rejected', value: 'rejected'},
                {label: 'Frozen', value: 'frozen'},
                {label: 'Require AML document', value: 'require_aml_document'},
                {label: 'Clarity around name mismatch', value: 'clarity_around_name_mismatch'},
                {label: 'Unknown', value: 'unknown'},
            ],
        }
    },
    methods: {
        ...mapActions(['set_currencies']),
        filterUsers (val, update, abort) {
            if (val.length < 3) {
                abort()
                return
            }
            User.list(`q=${val}`).then(response => {
                this.stringOptions = response
                update(() => {
                    this.users = this.stringOptions
                })
            }).catch(data => {
                abort()
            })
        },
        filterReferences (val, update, abort) {
            if (val === '') {
                update(() => {
                    this.filtered_references = this.references
                })
                return
            }
            update(() => {
                const needle = val.toLowerCase()
                this.filtered_references = this.references.filter(v => v.reference.toLowerCase().indexOf(needle) > -1)
            })
        },
        copied(message) {
            this.$q.notify({
                message: message,
                position: 'top'
            })
        },
    },
    created() {},
    mounted() {
        if (! this.currencies.length) {
            this.loading = true
            this.set_currencies().then(() => {this.loading = false}).catch(() => {this.loading = false})
        }
        this.TransactionObj.currency_acronym = this.TransactionObj.currency_acronym || this.currencies[0]['acronym']
    },
    watch: {
        currency(val) {
            this.payment_methods = this.outgoing_payment_methods
            if(val) {
                this.payment_methods = this.outgoing_payment_methods.filter(type => type.currency === this.TransactionObj.currency_acronym)
            }
        }
    },
    components: {},
}
</script>

